import React, { useEffect, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { iconBase, iconUrl, reportMissing, siteIconsEnabled, SITE_ICONS_CHANGED } from '../services/siteIcons';

/**
 * Website icons for vault rows (Standalone vault plan, Part 2). The rules are
 * in services/siteIcons.js; this file only wires them into React.
 */

// Icons that failed in this session: never re-requested from the CDN until a restart.
const failed = new Set();

/** The "Show website icons" setting and the icon base URL, live. */
export function useSiteIconBase() {
  const [enabled, setEnabled] = useState(siteIconsEnabled);
  const [base, setBase] = useState(null);

  useEffect(() => {
    const onChange = () => setEnabled(siteIconsEnabled());
    window.addEventListener(SITE_ICONS_CHANGED, onChange);
    return () => window.removeEventListener(SITE_ICONS_CHANGED, onChange);
  }, []);

  useEffect(() => {
    let alive = true;
    if (enabled) iconBase().then((b) => alive && setBase(b));
    else setBase(null);
    return () => {
      alive = false;
    };
  }, [enabled]);

  return enabled ? base : null;
}

/** urlHost → registrable domain (or null), by the shared core, for the given hosts. */
export function useSiteKeys(hosts, active) {
  const [keys, setKeys] = useState(() => new Map());
  const joined = active ? [...new Set(hosts.filter(Boolean))].sort().join('\n') : '';

  useEffect(() => {
    let alive = true;
    const list = joined ? joined.split('\n') : [];
    if (list.length === 0) {
      setKeys(new Map());
      return undefined;
    }
    invoke('vault_site_keys', { hosts: list })
      .then((got) => {
        if (alive) setKeys(new Map(list.map((h, i) => [h, (got && got[i]) || null])));
      })
      .catch(() => alive && setKeys(new Map()));
    return () => {
      alive = false;
    };
  }, [joined]);

  return keys;
}

/**
 * THE item icon — the list row and the detail header both render this, so
 * they show the same thing: the site's icon when there is one (same URL, same
 * session cache of failures), else the type icon. `px` is the image's size;
 * `size` the fallback icon's.
 */
export default function VaultSiteIcon({ base, siteKey, Fallback, size = 14, px = 16 }) {
  const [broken, setBroken] = useState(() => !siteKey || failed.has(siteKey));

  useEffect(() => {
    setBroken(!siteKey || failed.has(siteKey));
  }, [siteKey]);

  if (!base || !siteKey || broken) return <Fallback size={size} />;
  return (
    <img
      className="vault-site-icon"
      src={iconUrl(base, siteKey)}
      alt=""
      width={px}
      height={px}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      draggable={false}
      onError={() => {
        failed.add(siteKey);
        setBroken(true);
        reportMissing(siteKey);
      }}
    />
  );
}
